import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Fragment } from "react";
import { useRegistrantProfile } from "./api";
import type { ProfileField, ProfileSection, User } from "../../types/user";
import { userStatusColour, userStatusLabel } from "../../lib/labels";
import { formatDateTime } from "../../lib/format";
import { errorMessageFor } from "../../api/client";

const SECTION_TITLES: Record<ProfileSection, string> = {
  about: "About them",
  location: "Where they live",
  church: "Parish and jurisdiction",
  clergy: "Clergy details",
  laity: "Lay details",
};

// What a reviewer is checking comes first.
const SECTION_ORDER: ProfileSection[] = ["clergy", "laity", "church", "about", "location"];

interface Props {
  user: User | null;
  /** Shows Approve and Decline when the account is waiting on a decision. */
  canReview: boolean;
  onReview: (user: User, approved: boolean) => void;
  onClose: () => void;
}

function displayName(user: User): string {
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
}

function Answer({ value }: { value: string | null }) {
  return value === null ? (
    <Typography
      component="span"
      variant="body2"
      sx={{ color: "text.secondary", fontStyle: "italic" }}
    >
      Not answered
    </Typography>
  ) : (
    <Typography
      component="span"
      variant="body2"
      sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
    >
      {value}
    </Typography>
  );
}

function Section({ title, fields }: { title: string; fields: ProfileField[] }) {
  return (
    <Box component="section" aria-label={title} sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box
        component="dl"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "minmax(140px, 1fr) 2fr" },
          columnGap: 2,
          rowGap: { xs: 0.25, sm: 0.75 },
          m: 0,
        }}
      >
        {fields.map((field) => (
          <Fragment key={field.key}>
            <Typography
              component="dt"
              variant="body2"
              sx={{ color: "text.secondary", mt: { xs: 0.75, sm: 0 } }}
            >
              {field.label}
            </Typography>
            <Box component="dd" sx={{ m: 0 }}>
              <Answer value={field.value} />
            </Box>
          </Fragment>
        ))}
      </Box>
    </Box>
  );
}

export function UserProfileDialog({ user, canReview, onReview, onClose }: Props) {
  const profile = useRegistrantProfile(user?.id ?? null);
  const reviewable = canReview && user !== null && user.status !== "APPROVED";
  const data = profile.data;

  return (
    <Dialog open={user !== null} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle>{user ? displayName(user) : ""}</DialogTitle>
      <DialogContent dividers>
        {user && (
          <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
            <Chip
              label={userStatusLabel(user.status)}
              size="small"
              color={userStatusColour(user.status)}
            />
            {!user.emailConfirmed && (
              <Chip label="Email unconfirmed" size="small" variant="outlined" />
            )}
            {data?.submittedAt && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Registered {formatDateTime(data.submittedAt)}
              </Typography>
            )}
          </Stack>
        )}

        {user?.comments && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Comment on file:</strong> {user.comments}
          </Alert>
        )}

        {profile.isPending && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress aria-label="Loading profile" />
          </Box>
        )}

        {profile.isError && (
          <Alert severity="error">
            {errorMessageFor(profile.error) ?? "Could not load the profile."}
          </Alert>
        )}

        {data && !data.submitted && (
          <Alert severity="info">
            There is no registration form on file for this account. It may have been created by an
            administrator.
          </Alert>
        )}

        {data && data.changes.length > 0 && (
          <Box component="section" aria-label="Changes awaiting approval" sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
              Changes awaiting approval
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Now</TableCell>
                  <TableCell>Requested</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.changes.map((change) => (
                  <TableRow key={change.key}>
                    <TableCell>{change.label}</TableCell>
                    <TableCell>
                      <Answer value={change.current} />
                    </TableCell>
                    <TableCell>
                      <Answer value={change.proposed} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {data &&
          SECTION_ORDER.map((section) => {
            const fields = data.fields.filter((field) => field.section === section);
            return fields.length > 0 ? (
              <Section key={section} title={SECTION_TITLES[section]} fields={fields} />
            ) : null;
          })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {reviewable && user && (
          <>
            <Button variant="outlined" color="error" onClick={() => onReview(user, false)}>
              Decline
            </Button>
            <Button variant="contained" onClick={() => onReview(user, true)}>
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
